// ─────────────────────────────────────────────────────────────────────────────
// useClockSettings — GET/POST /api/rexy/settings. Shared core (2026-07-31).
//
// Writes the ONE stored setting — `timezone`, an IANA zone name (default
// Australia/Hobart). The backend clock (core/rexy_clock) reads it and serves
// every gate. The zone list comes from the browser's own IANA database
// (Intl.supportedValuesOf); the backend re-validates against zoneinfo — junk
// never lands.
//
// NO IP-GUESSING, EVER (the VPS is in Frankfurt): the only way this value
// changes is the user choosing it. MarketClock is the DISPLAY TWIN of this
// contract; this hook is the writer, on every surface that offers the control.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './authHeaders';
import { SETTINGS } from './endpoints';

export function ianaZones(): string[] {
  try {
    const anyIntl = Intl as any;
    if (typeof anyIntl.supportedValuesOf === 'function') {
      return anyIntl.supportedValuesOf('timeZone');
    }
  } catch { /* fall through to manual entry */ }
  return [];
}

export type ClockSaveState = 'idle' | 'saving' | 'saved' | 'error';

export type ClockSettingsFeed = {
  current: string | null;
  serverNow: string | null;
  choice: string;
  setChoice: (z: string) => void;
  state: ClockSaveState;
  err: string | null;
  schedStatus: string | null;
  zones: string[];
  save: () => void;
  canSave: boolean;
};

export function useClockSettings(): ClockSettingsFeed {
  const [current, setCurrent] = useState<string | null>(null);
  const [serverNow, setServerNow] = useState<string | null>(null);
  const [choice, setChoiceRaw] = useState<string>('');
  const [state, setState] = useState<ClockSaveState>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [schedStatus, setSchedStatus] = useState<string | null>(null);
  const zones = ianaZones();

  useEffect(() => {
    fetch(SETTINGS, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          setCurrent(j.timezone);
          setChoiceRaw(j.timezone);
          setServerNow(j.server_now_local);
        } else setErr((j && j.error) || 'unavailable');
      })
      .catch((e) => setErr(String(e)));
  }, []);

  // Changing the selection clears a prior saved/error banner, exactly as the
  // desktop control did inline.
  const setChoice = useCallback((z: string) => {
    setChoiceRaw(z);
    setState('idle');
  }, []);

  const save = useCallback(() => {
    setState('saving');
    setErr(null);
    fetch(SETTINGS, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ timezone: choice }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          setCurrent(j.timezone);
          setServerNow(j.server_now_local);
          setSchedStatus(j.scheduler || null);
          setState('saved');
          // GHOST HOLD item 2 — propagate the CONFIRMED zone to the display
          // twin (MarketClock) and any other listener, live, no reload.
          window.dispatchEvent(new CustomEvent('rexy:timezone', { detail: j.timezone }));
        } else {
          setState('error');
          setSchedStatus((j && j.scheduler) || null);
          setErr((j && j.error) || 'rejected');
        }
      })
      .catch((e) => { setState('error'); setErr(String(e)); });
  }, [choice]);

  return {
    current, serverNow, choice, setChoice, state, err, schedStatus, zones, save,
    canSave: !(state === 'saving' || !choice || choice === current),
  };
}
